import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    ArrowUpRight,
    Award,
    Blinds,
    Check,
    DoorOpen,
    FileText,
    Key,
    LockKeyhole,
    MapPin,
    Phone,
    ShieldCheck,
    Star,
    Timer,
    Vault,
} from "lucide-react";
import SEO from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import {
    ARRONDISSEMENTS,
    IMG,
    INSURANCES,
    LOCK_BRANDS,
    PHONE_FIXE_DISPLAY,
    PHONE_FIXE_TEL,
    REVIEWS,
    SERVICES,
    TARIFS,
    WHATSAPP_URL,
} from "../data/site";
import WhatsAppIcon from "../components/WhatsAppIcon";
import Marquee from "react-fast-marquee";

const SERVICE_ICONS = [DoorOpen, LockKeyhole, ShieldCheck, Blinds, Vault, Key];

// Numero d'urgence specifique a la page d'accueil uniquement (le reste du site
// garde PHONE_DISPLAY/PHONE_TEL issus de config.js).
const HOME_PHONE_DISPLAY = "06 27 14 20 00";
const HOME_PHONE_TEL = "tel:+33627142000";

const CARD_SHADOW = "shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)]";

const HERO_STATS = [
    { value: "Garantie", label: "Agréé assurance" },
    { value: "25 min", label: "Délai d'intervention" },
    { value: "24/7", label: "Nuit, week-end, fériés" },
    { value: "5/5", label: "Avis clients Google" },
];

const HERO_SERVICES = [
    "Ouverture de Porte",
    "Remplacement de serrure",
    "Réparation de serrure",
    "Blindage de Porte",
    "Agréé Assurance",
    "Dépannage Rideau Métallique",
    "Fabrication Rideau Métallique",
    "Dépannage Volet Roulant",
    "Intervention après cambriolage",
    "Service Coffre Fort",
];

const TRUST = [
    { icon: Timer, title: "Chez vous en 25 min", desc: "Intervention express 24h/24 sur Paris et l'Île-de-France." },
    { icon: ShieldCheck, title: "Agréé assurance", desc: "Prestations conformes aux exigences des compagnies d'assurance." },
    { icon: FileText, title: "Devis gratuit", desc: "Prix annoncé avant l'intervention, sans engagement ni surprise." },
    { icon: Award, title: "Artisan depuis 1985", desc: "Une équipe de serruriers passionnés et expérimentés." },
];

const MANIFESTO = [
    { title: "Conseil & assistance", desc: "Vous pouvez nous contacter par téléphone à tout moment pour demander conseil ou assistance suite à tout problème de serrurerie." },
    { title: "Disponibilité totale", desc: "Artisan serrurier disponible même tard la nuit, tôt le matin, les jours fériés et les fêtes, pour vous garantir le meilleur service SOS Serrurier Paris." },
    { title: "Rapidité d'intervention", desc: "Nous intervenons auprès de nos clients dans les 20 à 25 minutes suivant votre appel téléphonique." },
    { title: "Devis gratuit", desc: "Suite à votre appel, nous établissons avec vous un devis gratuit et sans engagement afin que vous puissiez estimer le coût de notre intervention." },
    { title: "Tarif serrurier pas cher", desc: "Nous vous offrons des services de serrurerie à des prix très compétitifs, sans jamais compromettre la qualité du travail." },
];

const STEPS = [
    { title: "Contact", desc: "Par téléphone, email ou formulaire de devis. Nos collaborateurs sont disponibles tous les jours, 24h/24, même les jours fériés." },
    { title: "Identification du besoin", desc: "Nous vous posons des questions précises pour identifier la cause du problème et trouver la solution adaptée." },
    { title: "Devis gratuit", desc: "Un devis gratuit et sans engagement détaillant le prix de chaque intervention et, si nécessaire, le tarif des pièces à changer." },
    { title: "Dépannage", desc: "Nos artisans se déplacent dans les 20 arrondissements de Paris et toute l'Île-de-France. Chez vous en 30 minutes." },
];

const ADVICE = [
    {
        title: "Un artisan qualifié ne propose pas de « tout changer »",
        desc: "Si votre serrure est réparable sans changement, un serrurier honnête vous le dira. Il prend le temps d'analyser le problème et de vous expliquer pourquoi il conseille tel ou tel travail.",
    },
    {
        title: "Un serrurier honnête fait toujours un devis",
        desc: "Au-delà de 150€ de prestation, un devis est obligatoire. Le bon serrurier vous le remet sans même attendre votre demande : précis, détaillé, sans mauvaise surprise.",
    },
    {
        title: "Un serrurier compétent ne profite pas de la détresse",
        desc: "Clés oubliées à l'intérieur, urgence nocturne : les escrocs font monter la facture parce qu'ils savent que vous n'avez pas le choix. Choisissez un serrurier qualifié en amont.",
    },
];


const SERVICE_DETAILS = [
    {
        title: "Ouverture de porte — sans dégradation",
        desc: "Porte claquée, clé oubliée à l'intérieur, serrure bloquée : nos serruriers utilisent des techniques de crochetage non destructif qui préservent votre porte et votre serrure après l'ouverture. Nous maîtrisons tous les cylindres du marché, des modèles standard aux multipoints haute sécurité A2P. Tarif dès 60 € TTC — artisan chez vous en 25 minutes en moyenne sur tout Paris.",
    },
    {
        title: "Blindage de porte",
        desc: "Une porte blindée est la protection la plus efficace contre l'effraction. Nous posons des portes blindées certifiées A2P (BP1, BP2, BP3) sur tous types d'encadrements, avec serrure multipoints haute sécurité et renforcement de cadre inclus. Prestation agréée assurance : facture détaillée fournie pour faciliter votre remboursement.",
    },
    {
        title: "Dépannage rideau métallique",
        desc: "Rideau bloqué, moteur en panne, lame déboîtée ou ressort cassé : nos techniciens interviennent en urgence sur tous les systèmes, commerciaux et industriels. Dépannage express le jour même, maintenance préventive, motorisation et remplacement complet de tablier. Disponible le soir, le week-end et les jours fériés.",
    },
    {
        title: "Changement de serrure",
        desc: "Après un cambriolage, une perte de clés ou un changement de locataire, remplacer sa serrure est la première mesure de sécurité. Nous posons des cylindres haute sécurité Fichet, Bricard, Mul-T-Lock, Vachette et DOM — avec ou sans garantie de non-reproduction des clés. Travaux conformes aux exigences des assureurs habitation.",
    },
    {
        title: "Service coffre-fort",
        desc: "Ouverture d'un coffre bloqué, installation neuve ou réparation de mécanisme défaillant : nos experts interviennent chez les particuliers et les professionnels. Toutes marques (Fichet, Hartmann, Chubbsafes…), tous systèmes — mécanique, électronique, biométrique ou à clé. Intervention discrète et sans dégradation garantie.",
    },
    {
        title: "Double de clés",
        desc: "Reproduction de clés plates, à gorges, magnétiques ou de sécurité protégées (Fichet F3D, Vachette Radial NT…), clés de voiture, de boîte aux lettres ou de cadenas. Fabrication immédiate pour les modèles courants, 24 à 48 h pour les clés brevetées. Service disponible en atelier ou à domicile sur rendez-vous.",
    },
];

const FAQ_ITEMS = [
    {
        q: "Quel est le tarif d'une ouverture de porte à Paris ?",
        a: "Le tarif démarre à 60 € TTC pour une ouverture en journée sur un cylindre standard. Le montant exact dépend du type de serrure, de l'horaire et de la complexité de l'intervention. Nous vous communiquons toujours le prix définitif par téléphone avant de nous déplacer — aucune surprise à l'arrivée.",
    },
    {
        q: "Êtes-vous disponibles la nuit et le week-end ?",
        a: "Oui, notre service fonctionne 24h/24 et 7j/7, nuits, week-ends et jours fériés compris. Le délai d'intervention reste identique quel que soit l'horaire : en moyenne 25 minutes sur Paris intramuros. Un artisan est toujours joignable par téléphone.",
    },
    {
        q: "Mon assurance habitation prend-elle en charge les frais ?",
        a: "Dans la majorité des cas, votre contrat multirisque habitation couvre les frais de serrurerie après cambriolage ou effraction. En tant que serrurier agréé assurance, nous établissons une facture détaillée conforme aux exigences de votre compagnie. Rapprochez-vous de votre assureur pour connaître les conditions exactes de votre contrat.",
    },
    {
        q: "Ma serrure sera-t-elle abîmée après l'ouverture ?",
        a: "Nous privilégions systématiquement les méthodes non destructives : crochetage, décodage, déverrouillage par l'extérieur. Dans l'immense majorité des cas, votre serrure reste parfaitement fonctionnelle après l'intervention. Si l'ouverture sans dégradation s'avère impossible, nous vous en informons avant d'agir et vous proposons un devis de remplacement.",
    },
    {
        q: "Quelles marques de serrures posez-vous ?",
        a: "Nous travaillons sur toutes les grandes marques : Fichet, Bricard, Vachette, Mul-T-Lock, JPM, DOM, Reelax, Keso et Abloy. Nos artisans sont formés aux dernières générations de cylindres haute sécurité et anti-effraction. Nous vous conseillons la solution adaptée à votre configuration et à votre budget.",
    },
    {
        q: "Comment éviter les arnaques à la serrurerie ?",
        a: "Un artisan sérieux vous donne toujours une fourchette de prix par téléphone avant l'intervention et remet un devis écrit sur place avant tout travail — obligatoire légalement au-delà de 150 € TTC. Il remet aussi une facture détaillée à la fin. Méfiez-vous des annonces à 25 ou 30 € : elles servent d'appâts et dissimulent des pratiques frauduleuses.",
    },
];

export default function Home() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

    return (
        <div data-testid="home-page">
            <SEO
                title="Serrurier Paris — Ouverture de Porte 60€ TTC | Urgence 24/7 · 30 min"
                description="Serrurier Paris pas cher : ouverture de porte dès 60€ TTC, dépannage serrurerie 24h/24 sur Paris & Île-de-France. Artisan agréé assurance, intervention en 30 min, devis gratuit et transparent. ☎ 06 27 14 20 00"
                path="/"
            />

            {/* HERO */}
            <section ref={heroRef} className="grain relative overflow-hidden bg-midnight">
                <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
                    <motion.img
                        src={IMG("bon-serrurier-paris.webp")}
                        alt="Serrurier Paris — artisan en intervention sur une serrure, dépannage 24h/24"
                        className="h-full w-full object-cover"
                        width="1200"
                        height="800"
                        loading="eager"
                        fetchPriority="high"
                        initial={{ scale: 1.15, opacity: 0.6 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
                    />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-r from-midnight via-midnight/90 to-midnight/55" />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight via-transparent to-midnight/40" />

                <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 md:py-14 lg:px-8">
                    <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-14">
                        {/* COLONNE GAUCHE */}
                        <div>
                            <FadeUp delay={0.15}>
                                <StatusBadge />
                            </FadeUp>
                            <motion.p
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 0.3 }}
                                className="mb-5 mt-3 inline-flex items-center gap-2 border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-gold"
                                data-testid="hero-badge"
                            >
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold" />
                                Service Dépannage d'urgence — Paris & Île-de-France Depuis 1985
                            </motion.p>

                            <h1 className="font-display text-4xl font-extrabold leading-[1.02] tracking-tighter text-white drop-shadow-2xl sm:text-5xl lg:text-[3.4rem]">
                                <MaskedLines delay={0.35} lines={["Serrurier Paris", "pas cher"]} />
                                <MaskedLines
                                    delay={0.35 + 2 * 0.14}
                                    lines={["Ouverture de porte", "60€ TTC."]}
                                    lineClassName="text-[#f97316]"
                                />
                            </h1>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.9 }}
                                className="mt-5 max-w-xl text-base leading-relaxed text-white/85"
                            >
                                Porte claquée, clé perdue, serrure bloquée : un artisan serrurier agréé assurance chez
                                vous en 25 minutes, 24h/24 et 7j/7. Devis gratuit, prix annoncé avant l'intervention.
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 1.05 }}
                                className="mt-7 grid gap-3 sm:grid-cols-2"
                            >
                                <a
                                    href={HOME_PHONE_TEL}
                                    data-testid="hero-call-button"
                                    className="group flex items-center justify-center gap-3 bg-emergency px-6 py-4 font-bold text-white transition-all duration-300 hover:bg-emergency-dark"
                                >
                                    <Phone className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:-rotate-12" />
                                    <span className="leading-none">
                                        <span className="block text-[10px] font-semibold uppercase tracking-[0.2em] opacity-80">
                                            Appel d'urgence — intervention 30 min
                                        </span>
                                        <span className="mt-1 block text-lg">{HOME_PHONE_DISPLAY}</span>
                                    </span>
                                </a>
                                <a
                                    href={PHONE_FIXE_TEL}
                                    data-testid="hero-fixe-button"
                                    className="group flex items-center justify-center gap-3 bg-gold px-6 py-4 font-bold text-midnight transition-all duration-300 hover:bg-white"
                                >
                                    <Phone className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:-rotate-12" />
                                    <span className="leading-none">
                                        <span className="block text-[10px] font-semibold uppercase tracking-[0.2em] opacity-80">
                                            Devis gratuit — ligne directe
                                        </span>
                                        <span className="mt-1 block text-lg">{PHONE_FIXE_DISPLAY}</span>
                                    </span>
                                </a>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 1.2 }}
                                className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4"
                                data-testid="hero-stats"
                            >
                                {HERO_STATS.map((s) => (
                                    <div
                                        key={s.label}
                                        className="rounded-xl border border-white/10 bg-white/5 px-4 py-4 backdrop-blur-sm"
                                    >
                                        <p className="font-display text-2xl font-extrabold tracking-tight text-gold">{s.value}</p>
                                        <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-white/60">{s.label}</p>
                                    </div>
                                ))}
                            </motion.div>
                        </div>

                        {/* COLONNE DROITE */}
                        <motion.div
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                            className="space-y-4"
                        >
                            <div className="overflow-hidden rounded-xl border border-white/15 shadow-2xl">
                                <img
                                    src={IMG("serrurier-paris.paris.webp")}
                                    alt="Porte claquée ? Porte bloquée ? Serrurier Paris pas cher — ouverture de porte 60€ TTC"
                                    className="w-full object-contain"
                                    width="800"
                                    height="480"
                                />
                            </div>
                            <div className="rounded-xl border border-white/10 bg-midnight/70 p-6 backdrop-blur" data-testid="hero-services-list">
                                <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                                    {HERO_SERVICES.map((s) => (
                                        <span key={s} className="flex items-center gap-2 text-sm font-semibold text-white/75">
                                            <Check className="h-4 w-4 shrink-0 text-gold" /> {s}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <EditorialMarquee />

            {/* RÉASSURANCE */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <div className="grid gap-px bg-midnight/10 sm:grid-cols-2 lg:grid-cols-4">
                    {TRUST.map((t, i) => (
                        <FadeUp key={t.title} delay={i * 0.08} className="bg-white">
                            <div className="group h-full p-8 transition-colors duration-300 hover:bg-midnight" data-testid={`trust-card-${i}`}>
                                <t.icon className="h-8 w-8 text-emergency transition-colors duration-300 group-hover:text-gold" strokeWidth={1.8} />
                                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight transition-colors duration-300 group-hover:text-white">
                                    {t.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-midnight/60 transition-colors duration-300 group-hover:text-white/60">
                                    {t.desc}
                                </p>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </section>

            {/* SERVICES BENTO */}
            <section className="bg-white py-16 md:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Nos services</SectionLabel>
                        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
                            <h2 className="max-w-3xl font-display text-4xl font-medium tracking-tight md:text-5xl">
                                6 services de dépannage indispensables
                            </h2>
                            <Link
                                to="/tarifs"
                                data-testid="services-tarifs-link"
                                className="group flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-emergency"
                            >
                                Voir tous les tarifs
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                            </Link>
                        </div>
                    </FadeUp>

                    <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {SERVICES.map((s, i) => {
                            const Icon = SERVICE_ICONS[i];
                            if (i === 0) {
                                return (
                                    <FadeUp key={s.title} delay={0} className="lg:col-span-2">
                                        <Link
                                            to={s.slug}
                                            data-testid={`service-card-${i}`}
                                            className="group flex h-full flex-col overflow-hidden border border-midnight/10 bg-cloud transition-all duration-300 hover:-translate-y-2 hover:border-midnight hover:bg-white hover:shadow-[0_28px_60px_-20px_rgba(10,17,40,0.35)] md:flex-row"
                                        >
                                            <div className="flex flex-1 flex-col justify-between p-8">
                                                <div className="flex items-start justify-between">
                                                    <span className="flex h-12 w-12 items-center justify-center bg-midnight/5 text-emergency transition-colors duration-300 group-hover:bg-emergency group-hover:text-white">
                                                        <Icon className="h-6 w-6" strokeWidth={1.8} />
                                                    </span>
                                                    <span className="bg-midnight px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gold">
                                                        {s.price}
                                                    </span>
                                                </div>
                                                <div className="mt-8">
                                                    <h3 className="font-display text-2xl font-semibold tracking-tight">{s.title}</h3>
                                                    <p className="mt-3 max-w-md text-sm leading-relaxed text-midnight/60">{s.desc}</p>
                                                    <span className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-emergency">
                                                        Découvrir
                                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex h-48 w-full shrink-0 items-center justify-center bg-midnight/5 p-4 md:h-auto md:w-72 lg:w-96">
                                                <img
                                                    src={IMG("serrurier-ouverture-de-porte.webp")}
                                                    alt="Ouverture de porte — artisan serrurier en intervention à Paris"
                                                    className="h-full w-full object-contain"
                                                    width="500"
                                                    height="300"
                                                    loading="lazy"
                                                />
                                            </div>
                                        </Link>
                                    </FadeUp>
                                );
                            }
                            return (
                                <FadeUp key={s.title} delay={(i % 3) * 0.08}>
                                    <Link
                                        to={s.slug}
                                        data-testid={`service-card-${i}`}
                                        className="group flex h-full flex-col justify-between border border-midnight/10 bg-cloud p-8 transition-all duration-300 hover:-translate-y-2 hover:border-midnight hover:bg-white hover:shadow-[0_28px_60px_-20px_rgba(10,17,40,0.35)]"
                                    >
                                        <div className="flex items-start justify-between">
                                            <span className="flex h-12 w-12 items-center justify-center bg-midnight/5 text-emergency transition-colors duration-300 group-hover:bg-emergency group-hover:text-white">
                                                <Icon className="h-6 w-6" strokeWidth={1.8} />
                                            </span>
                                            <span className="bg-midnight px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gold">
                                                {s.price}
                                            </span>
                                        </div>
                                        <div className="mt-8">
                                            <h3 className="font-display text-2xl font-semibold tracking-tight">{s.title}</h3>
                                            <p className="mt-3 max-w-md text-sm leading-relaxed text-midnight/60">{s.desc}</p>
                                            <span className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-emergency">
                                                Découvrir
                                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                            </span>
                                        </div>
                                    </Link>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>


            {/* DÉTAIL DES INTERVENTIONS */}
            <section className="bg-cloud py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Chaque prestation, maîtrisée</SectionLabel>
                        <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-tight md:text-5xl">
                            Nos 6 interventions clés en détail
                        </h2>
                        <p className="mt-6 max-w-2xl leading-relaxed text-midnight/70">
                            De l'ouverture de porte d'urgence au blindage haut de gamme, nos artisans couvrent l'intégralité des besoins en serrurerie résidentielle et commerciale. Chaque intervention est réalisée dans les règles de l'art, avec des pièces certifiées et une garantie sur les travaux effectués.
                        </p>
                    </FadeUp>
                    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {SERVICE_DETAILS.map((s, i) => (
                            <FadeUp key={s.title} delay={i * 0.06}>
                                <div className="h-full bg-white p-8 shadow-sm" data-testid={`service-detail-${i}`}>
                                    <h3 className="font-display text-xl font-semibold tracking-tight">{s.title}</h3>
                                    <p className="mt-4 text-sm leading-relaxed text-midnight/65">{s.desc}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* ÉDITORIAL — INTERVENTION PRO */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-28 lg:px-8">
                <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
                    <FadeUp>
                        <div className="relative">
                            <img
                                src={IMG("tarif-serrurier.webp")}
                                alt="Serrurier Paris — crochetage de serrure par un artisan"
                                className={`aspect-[4/5] w-full rounded-xl object-cover ${CARD_SHADOW}`}
                                width="800"
                                height="1000"
                                loading="lazy"
                            />
                            <img
                                src={IMG("serrure-depannage-paris.webp")}
                                alt="Serrures et coffres-forts toutes marques — dépannage Paris"
                                className="absolute -bottom-8 -right-4 hidden w-44 rounded-xl border-8 border-cloud bg-white object-cover shadow-2xl sm:block md:w-56"
                                width="400"
                                height="300"
                                loading="lazy"
                            />
                            <div className="absolute -left-4 top-8 bg-emergency px-5 py-4 text-white shadow-xl md:-left-8">
                                <p className="font-display text-3xl font-semibold leading-none">25<span className="text-lg">min</span></p>
                                <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] opacity-85">chez vous</p>
                            </div>
                        </div>
                    </FadeUp>
                    <div>
                        <FadeUp>
                            <SectionLabel>Les experts du dépannage</SectionLabel>
                            <h2 className="mt-4 font-display text-4xl font-medium tracking-tight md:text-5xl">
                                Optez pour une intervention professionnelle
                            </h2>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <p className="mt-6 leading-relaxed text-midnight/70">
                                Afin de vivre dans un monde plus sûr, il est recommandé de vous assurer de la fiabilité
                                de vos portes, de la qualité de vos serrures et des différents dispositifs anti-vol.
                                Pour être sécurisé au maximum, il vaut mieux faire appel à une entreprise de dépannage
                                de serrurerie qui vous assure un service rapide et un travail irréprochable.
                            </p>
                            <p className="mt-4 leading-relaxed text-midnight/70">
                                Serrurier Paris est une équipe d'artisans passionnés par leur métier. Une intervention
                                rapide, entre 20 et 25 minutes, sur Paris et les départements 93, 94, 92, 95, 77, 78, 91
                                pour toute ouverture de porte, changement de serrure ou dépannage de rideau métallique.
                            </p>
                            <p className="mt-4 leading-relaxed text-midnight/70">
                                Un cambriolage a lieu toutes les deux minutes en France : nous mettons en avant la
                                sécurité et une qualité de prestation défiant toute concurrence, avec des conseils
                                avisés pour entretenir vos serrures et allonger leur durée de vie.
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.18}>
                            <div className="mt-8 grid grid-cols-2 gap-3 text-sm font-semibold">
                                {["Ouverture de porte", "Remplacement de serrure", "Réparation de serrure", "Blindage de porte", "Intervention après cambriolage", "Agréé assurance"].map((item) => (
                                    <span key={item} className="flex items-center gap-2 text-midnight/80">
                                        <Check className="h-4 w-4 shrink-0 text-emergency" /> {item}
                                    </span>
                                ))}
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* MANIFESTO NUMÉROTÉ */}
            <section className="grain relative bg-midnight py-16 text-white md:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel dark>Pourquoi nous choisir</SectionLabel>
                        <h2 className="mt-4 max-w-2xl font-display text-4xl font-medium tracking-tight md:text-5xl">
                            La garantie d'un travail soigné, au juste prix
                        </h2>
                    </FadeUp>
                    <div className="mt-14">
                        {MANIFESTO.map((m, i) => (
                            <FadeUp key={m.title} delay={i * 0.05}>
                                <div
                                    data-testid={`manifesto-item-${i}`}
                                    className="group grid gap-4 border-t border-white/10 py-8 transition-colors duration-300 last:border-b hover:bg-white/[0.03] md:grid-cols-[120px_1fr_2fr] md:items-baseline md:gap-8 md:px-6"
                                >
                                    <span className="font-display text-4xl font-semibold text-gold/40 transition-colors duration-300 group-hover:text-gold md:text-5xl">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="font-display text-2xl font-medium tracking-tight md:text-3xl">{m.title}</h3>
                                    <p className="max-w-xl leading-relaxed text-white/60">{m.desc}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERRURIER DE NUIT / SOS */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-28 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
                    <FadeUp>
                        <img
                            src={`${process.env.PUBLIC_URL}/serrurier-nuit.webp`}
                            alt="Serrurier de nuit à Paris — artisan ouvrant une serrure, photo d'intervention nocturne"
                            className={`aspect-[4/3] w-full rounded-xl object-cover ${CARD_SHADOW}`}
                            width="800"
                            height="600"
                            loading="lazy"
                            data-testid="night-image"
                        />
                        <h2 className="mt-8 font-display text-3xl font-medium tracking-tight md:text-4xl">
                            Serrurier de nuit à Paris
                        </h2>
                        <p className="mt-4 leading-relaxed text-midnight/70">
                            En rentrant la nuit, vous réalisez que vous avez oublié ou perdu votre clé. Les portes
                            d'aujourd'hui sont de plus en plus fiables et difficiles à ouvrir sans clé. Votre seul
                            secours : une équipe spécialisée en dépannage, serrurier de nuit pas cher travaillant
                            24h/24 sur Paris et toute l'Île-de-France — y compris le samedi et le dimanche.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.12}>
                        <div className={`flex aspect-[4/3] w-full items-center justify-center rounded-xl bg-white p-6 ${CARD_SHADOW}`} data-testid="presentation-card-idf">
                            <img
                                src={IMG("idf.webp")}
                                alt="Carte d'intervention — Paris et Île-de-France"
                                className="max-h-full w-full object-contain"
                                width="800"
                                height="600"
                                loading="lazy"
                            />
                        </div>
                        <h2 className="mt-8 font-display text-3xl font-medium tracking-tight md:text-4xl">
                            Serrurier dépannage express
                        </h2>
                        <p className="mt-4 leading-relaxed text-midnight/70">
                            Notre service SOS Serrurier Paris est disponible à tout moment, sans interruption le
                            week-end ni les jours fériés. Changement, pose ou réparation de toutes marques de
                            serrures, poignées, verrous, canons simples ou doubles, portes blindées, gâches
                            électriques… On ne peut pas prévenir la perte de clés ou un cambriolage : nous sommes
                            toujours présents pour nos clients.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* ÉTAPES */}
            <section className="bg-white py-16 md:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Comment ça marche</SectionLabel>
                        <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-tight md:text-5xl">
                            Les étapes de votre dépannage
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-px bg-midnight/10 sm:grid-cols-2 lg:grid-cols-4">
                        {STEPS.map((s, i) => (
                            <FadeUp key={s.title} delay={i * 0.08} className="bg-white">
                                <div className="h-full p-8" data-testid={`step-card-${i}`}>
                                    <span className="font-display text-5xl font-extrabold text-midnight/30">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{s.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-midnight/60">{s.desc}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>


            {/* AGRÉÉ ASSURANCE */}
            <section className="bg-cloud py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid items-center gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-20">
                        <FadeUp>
                            <SectionLabel>Remboursement facilité</SectionLabel>
                            <h2 className="mt-4 font-display text-4xl font-medium tracking-tight md:text-5xl">
                                Serrurier agréé assurance à Paris
                            </h2>
                            <p className="mt-6 leading-relaxed text-midnight/70">
                                En cas de cambriolage, d'effraction ou de dégradation de votre serrurerie, votre assurance multirisque habitation peut prendre en charge tout ou partie de l'intervention. En tant que serrurier agréé assurance, nous établissons une facture détaillée — nature des travaux, référence des pièces posées, montant TTC — conforme aux exigences de votre compagnie d'assurance.
                            </p>
                            <p className="mt-4 leading-relaxed text-midnight/70">
                                Avant de commencer tout travail, un devis écrit vous est systématiquement remis, précisant l'ensemble des prestations et le coût total. Ce document est indispensable pour déclencher votre prise en charge. Nos artisans sont reconnus par Allianz, AXA, Groupama, MAAF, Générali, MACIF, MAIF, April et la majorité des mutuelles habitation.
                            </p>
                            <p className="mt-4 leading-relaxed text-midnight/70">
                                Le remboursement dépend des garanties souscrites dans votre contrat. Nous vous recommandons de contacter votre assureur pour connaître votre couverture. Notre équipe peut, à votre demande, vous accompagner dans la constitution de votre dossier sinistre : rapport d'intervention, photos, facture détaillée.
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.12}>
                            <div className="divide-y divide-midnight/10 bg-white p-8 shadow-sm">
                                {[
                                    { n: "01", title: "Devis avant travaux", desc: "Remis systématiquement avant toute intervention dépassant 150 € TTC — c'est une obligation légale que nous respectons scrupuleusement." },
                                    { n: "02", title: "Facture conforme", desc: "Facture détaillant pièces, main-d'œuvre et montants TTC, directement utilisable pour votre déclaration de sinistre auprès de votre assureur." },
                                    { n: "03", title: "Marques certifiées", desc: "Toutes nos serrures et cylindres proviennent de marques agréées assurance : Fichet, Bricard, Mul-T-Lock, Vachette, DOM et autres marques reconnues." },
                                    { n: "04", title: "Rapport d'intervention", desc: "Document technique disponible sur demande, décrivant le problème constaté et les travaux réalisés — utile pour votre dossier de sinistre." },
                                ].map((item) => (
                                    <div key={item.title} className="flex items-start gap-5 py-6 first:pt-0 last:pb-0">
                                        <span className="font-display text-2xl font-bold text-gold/50">{item.n}</span>
                                        <div>
                                            <h3 className="font-semibold">{item.title}</h3>
                                            <p className="mt-1 text-sm leading-relaxed text-midnight/60">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* TARIFS TEASER */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-28 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
                    <FadeUp>
                        <SectionLabel>Tarifs transparents</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-medium tracking-tight md:text-5xl">
                            Le prix annoncé est le prix payé
                        </h2>
                        <p className="mt-6 leading-relaxed text-midnight/70">
                            Devis gratuit et sans engagement avant chaque intervention. Au-delà de 150€ de travaux,
                            un devis écrit vous est systématiquement remis. Nos tarifs sont TTC et conformes aux
                            exigences des compagnies d'assurance.
                        </p>
                        <Link
                            to="/tarifs"
                            data-testid="tarifs-teaser-link"
                            className="mt-8 inline-flex items-center gap-2 bg-midnight px-7 py-4 font-bold text-white transition-colors duration-300 hover:bg-navy"
                        >
                            Grille tarifaire complète <ArrowRight className="h-4 w-4" />
                        </Link>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <div className="divide-y divide-midnight/10 border border-midnight/10 bg-white" data-testid="tarifs-teaser-table">
                            {TARIFS.slice(0, 6).map((t) => (
                                <div key={t.service} className="group flex items-center justify-between gap-4 px-6 py-5 transition-colors hover:bg-cloud">
                                    <div>
                                        <p className="font-semibold">{t.service}</p>
                                        <p className="text-xs text-midnight/50">{t.note}</p>
                                    </div>
                                    <span className="whitespace-nowrap font-display text-2xl font-semibold text-emergency">
                                        {t.price}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* AVIS */}
            <section className="bg-white py-16 md:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <div className="flex flex-wrap items-end justify-between gap-6">
                            <div>
                                <SectionLabel>Avis clients</SectionLabel>
                                <h2 className="mt-4 font-display text-4xl font-medium tracking-tight md:text-5xl">
                                    Ils nous font confiance
                                </h2>
                            </div>
                            <div className="flex items-center gap-3 border border-midnight/10 px-5 py-3" data-testid="google-rating-badge">
                                <span className="flex text-gold">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="h-5 w-5 fill-gold" />
                                    ))}
                                </span>
                                <span className="text-sm font-bold">Avis Google 5/5</span>
                            </div>
                        </div>
                    </FadeUp>
                    <div className="mt-12 grid gap-5 md:grid-cols-3">
                        {REVIEWS.map((r, i) => (
                            <FadeUp key={r.name} delay={i * 0.08}>
                                <figure
                                    data-testid={`review-card-${i}`}
                                    className="flex h-full flex-col justify-between border border-midnight/10 bg-cloud p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                                >
                                    <div>
                                        <span className="flex text-gold">
                                            {[...Array(5)].map((_, j) => (
                                                <Star key={j} className="h-4 w-4 fill-gold" />
                                            ))}
                                        </span>
                                        <blockquote className="mt-5 leading-relaxed text-midnight/80">« {r.text} »</blockquote>
                                    </div>
                                    <figcaption className="mt-6 flex items-center gap-3">
                                        <span className="flex h-10 w-10 items-center justify-center bg-midnight font-display text-sm font-semibold text-gold">
                                            {r.name.charAt(0)}
                                        </span>
                                        <span>
                                            <span className="block text-sm font-bold">{r.name}</span>
                                            <span className="block text-xs text-midnight/50">{r.place}</span>
                                        </span>
                                    </figcaption>
                                </figure>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* ARRONDISSEMENTS */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-28 lg:px-8">
                <FadeUp>
                    <SectionLabel>Zones d'intervention</SectionLabel>
                    <h2 className="mt-4 max-w-2xl font-display text-4xl font-medium tracking-tight md:text-5xl">
                        Dépannage serrurerie dans les 20 arrondissements
                    </h2>
                    <p className="mt-6 max-w-2xl leading-relaxed text-midnight/70">
                        Nos serruriers se déplacent chaque jour, 24h/24, pour des interventions d'ouverture de porte
                        dans tous les arrondissements parisiens et l'ensemble de l'Île-de-France : 75, 77, 78, 91,
                        92, 93, 94, 95.
                    </p>
                </FadeUp>
                <div className="mt-10 grid grid-cols-2 gap-px bg-midnight/10 sm:grid-cols-4 lg:grid-cols-5" data-testid="arrondissements-grid">
                    {ARRONDISSEMENTS.map((a) => (
                        <Link
                            key={a.n}
                            to={`/serrurier-paris-${a.n}`}
                            data-testid={`arrondissement-link-${a.n}`}
                            className={`group flex items-center justify-between px-5 py-4 transition-colors ${
                                a.n % 2 === 0 ? "bg-white text-midnight/70 hover:bg-cloud" : "bg-cloud text-midnight/70 hover:bg-white"
                            }`}
                        >
                            <span className="font-semibold">Serrurier {a.label}</span>
                            <ArrowUpRight className="h-4 w-4 text-midnight/25 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-emergency" />
                        </Link>
                    ))}
                </div>
            </section>

            {/* MARQUES + ASSURANCES */}
            <section className="border-y border-midnight/10 bg-white py-14" data-testid="brands-strip">
                <p className="mb-8 text-center text-xs font-bold uppercase tracking-[0.25em] text-midnight/40">
                    Remplacement de serrures toutes marques — agréé par les assurances
                </p>
                <Marquee speed={24} gradient={false} pauseOnHover>
                    {[...LOCK_BRANDS, ...INSURANCES].map((brand, i) => (
                        <span
                            key={`${brand}-${i}`}
                            className="mx-8 font-display text-xl font-semibold uppercase tracking-[0.2em] text-midnight/30 transition-colors duration-300 hover:text-midnight md:mx-12 md:text-2xl"
                        >
                            {brand}
                        </span>
                    ))}
                </Marquee>
            </section>

            {/* CONSEILS HONNÊTETÉ */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-28 lg:px-8">
                <FadeUp>
                    <SectionLabel>Conseils d'experts</SectionLabel>
                    <h2 className="mt-4 max-w-2xl font-display text-4xl font-medium tracking-tight md:text-5xl">
                        À la recherche d'un bon serrurier honnête à Paris ?
                    </h2>
                </FadeUp>
                <div className="mt-12 grid gap-5 md:grid-cols-3">
                    {ADVICE.map((a, i) => (
                        <FadeUp key={a.title} delay={i * 0.08}>
                            <div className="h-full border-l-2 border-gold bg-white p-8" data-testid={`advice-card-${i}`}>
                                <h3 className="font-display text-xl font-semibold leading-snug tracking-tight">{a.title}</h3>
                                <p className="mt-4 text-sm leading-relaxed text-midnight/60">{a.desc}</p>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </section>


            {/* FAQ */}
            <section className="bg-cloud py-16 md:py-28">
                <div className="mx-auto max-w-4xl px-4 sm:px-6">
                    <FadeUp>
                        <SectionLabel>Questions fréquentes</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-medium tracking-tight md:text-5xl">
                            Tout ce que vous devez savoir avant d'appeler
                        </h2>
                    </FadeUp>
                    <div className="mt-12 divide-y divide-midnight/10">
                        {FAQ_ITEMS.map((item, i) => (
                            <FadeUp key={item.q} delay={i * 0.05}>
                                <div className="py-8" data-testid={`faq-item-${i}`}>
                                    <h3 className="font-display text-xl font-semibold tracking-tight">{item.q}</h3>
                                    <p className="mt-4 leading-relaxed text-midnight/65">{item.a}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* ENTRETIEN */}
            <section className="bg-white py-16 md:py-28">
                <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                    <div>
                        <FadeUp>
                            <SectionLabel>Maintenance annuelle</SectionLabel>
                            <h2 className="mt-4 font-display text-4xl font-medium tracking-tight md:text-5xl">
                                Entretien régulier de serrurerie
                            </h2>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <p className="mt-6 leading-relaxed text-midnight/70">
                                Pour garantir la pérennité de vos portes et la sécurité de vos biens, une maintenance
                                préventive de vos serrures est recommandée. Une ou deux petites interventions par an
                                suffisent à oublier les portes qui grincent et les clés difficiles.
                            </p>
                            <p className="mt-4 leading-relaxed text-midnight/70">
                                Au titre de cette maintenance : diagnostic complet de votre serrurerie, réglage de
                                gâches, lubrification des cylindres, vérification des paumelles, dégrippage des
                                vieilles serrures, reproduction de jeux de clés, changement de tringle, modification
                                de verrous à tirette…
                            </p>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.12}>
                        <img
                            src={IMG("entretien-regulier-serrurerie.webp")}
                            alt="Entretien régulier de serrurerie à Paris"
                            className={`aspect-[4/3] w-full rounded-xl object-cover ${CARD_SHADOW}`}
                            width="800"
                            height="600"
                            loading="lazy"
                        />
                    </FadeUp>
                </div>
            </section>

            {/* CTA FINAL */}
            <section className="grain relative overflow-hidden bg-midnight py-20 text-white md:py-28">
                <div className="pointer-events-none absolute -right-20 -top-20 h-96 w-96 rounded-full bg-emergency/20 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
                <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
                    <FadeUp>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Urgence serrurerie ?</p>
                        <h2 className="mt-4 font-display text-4xl font-semibold tracking-tighter md:text-6xl">
                            Un artisan chez vous
                            <br />
                            en <span className="text-gold">30 minutes</span>.
                        </h2>
                        <p className="mx-auto mt-6 max-w-xl leading-relaxed text-white/70">
                            Devis gratuit par téléphone, prix transparent annoncé avant l'intervention. Paris et
                            toute l'Île-de-France, 24h/24 et 7j/7.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                            <a
                                href={HOME_PHONE_TEL}
                                data-testid="final-call-button"
                                className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors duration-300 hover:bg-emergency-dark"
                            >
                                <Phone className="h-5 w-5" /> {HOME_PHONE_DISPLAY}
                            </a>
                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="final-whatsapp-button"
                                className="flex items-center gap-3 bg-[#25D366] px-8 py-4 text-lg font-bold text-white transition-colors duration-300 hover:bg-[#1eb856]"
                            >
                                <WhatsAppIcon className="h-5 w-5" /> WhatsApp
                            </a>
                        </div>
                        <p className="mt-8 flex items-center justify-center gap-2 text-sm text-white/50">
                            <MapPin className="h-4 w-4" /> Paris 75 · 77 · 78 · 91 · 92 · 93 · 94 · 95
                        </p>
                    </FadeUp>
                </div>
            </section>
        </div>
    );
}
